import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { equipmentRequestsDetailService } from '../../detail/services/equipmentRequestsDetail.service';
import { equipmentRequestsEditService } from '../services/equipmentRequestsEdit.service';
import { equipmentRequestsNewService } from '../../new/services/equipmentRequestsNew.service';
import { RequestsNewService } from '@/modules/inventory/requests/new/services/RequestsNew.service';

export function useEquipmentRequestsEdit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id || '';
  const companyId = profile?.company_id || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    neededFrom: '',
    neededUntil: '',
    priority: 'normal' as any,
    items: [] as any[],
  });

  const detailQuery = useQuery({
    queryKey: ['equipment', 'requests', 'detail', id],
    queryFn: () => equipmentRequestsDetailService.getRequest(id, userId),
    enabled: !!id && !!userId,
  });

  const projectsQuery = useQuery({
    queryKey: ['projects', 'simple-list', companyId],
    queryFn: () => RequestsNewService.getProjects(companyId),
    enabled: !!companyId,
  });

  const categoriesQuery = useQuery({
    queryKey: ['equipment', 'categories', companyId],
    queryFn: () => equipmentRequestsNewService.getEquipmentCategories(companyId),
    enabled: !!companyId,
  });

  useEffect(() => {
    if (detailQuery.data) {
      setFormData({
        title: detailQuery.data.title,
        description: detailQuery.data.description || '',
        projectId: detailQuery.data.project_id || '',
        neededFrom: detailQuery.data.needed_from ? new Date(detailQuery.data.needed_from).toISOString().split('T')[0] : '',
        neededUntil: detailQuery.data.needed_until ? new Date(detailQuery.data.needed_until).toISOString().split('T')[0] : '',
        priority: detailQuery.data.priority,
        items: (detailQuery.data.items || []).map((i: any) => ({
          ...i,
          equipmentTypeId: i.equipment_type_id,
          categoryName: i.equipment_type?.name
        })),
      });
    }
  }, [detailQuery.data]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => equipmentRequestsEditService.updateRequest(userId, id, { ...data, companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', 'requests'] });
      toast.success('Đã cập nhật yêu cầu');
      navigate(`/equipment/requests/detail/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Không thể cập nhật');
    }
  });

  const addItem = (item: any) => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...item, id: Math.random().toString(36).substring(7) }]
    }));
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId)
    }));
  };

  return {
    formData,
    projects: projectsQuery.data || [],
    categories: categoriesQuery.data || [],
    isLoading: detailQuery.isLoading || projectsQuery.isLoading || categoriesQuery.isLoading,
    isSaving: updateMutation.isPending,
    setFormData,
    addItem,
    removeItem,
    updateRequest: () => updateMutation.mutate(formData),
  };
}
